import { createTheme } from "@mui/material/styles";

declare module "@mui/material/styles" {
  interface Palette {
    sidebar: { background: string; text: string; textActive: string };
  }
  interface PaletteOptions {
    sidebar?: { background?: string; text?: string; textActive?: string };
  }
}

const INK = "#2B2F42";
const OUTLINE = "#9AA0B0";
const CARD_SHADOW = "0 1px 2px rgba(43, 47, 66, 0.06)";

export const theme = createTheme({
  palette: {
    primary: {
      main: "#0F9E8E",
      light: "#3EC4B6",
      dark: "#0B7F73",
      contrastText: "#FFFFFF",
    },
    secondary: {
      main: "#F2A03C",
      light: "#F5B96B",
      dark: "#DB8A26",
      contrastText: "#FFFFFF",
    },
    background: {
      default: "#EFF1F5",
      paper: "#FFFFFF",
    },
    text: {
      primary: INK,
      secondary: "#5A6072",
      disabled: OUTLINE,
    },
    divider: "#DFE3EA",
    sidebar: {
      background: INK,
      text: "#FFFFFF",
      textActive: "#FFFFFF",
    },
    error: {
      main: "#E8382C",
    },
    success: {
      main: "#17733F",
    },
    warning: {
      main: "#F2A03C",
      dark: "#DB8A26",
      contrastText: "#FFFFFF",
    },
  },
  shape: { borderRadius: 8 },
  typography: {
    fontFamily: "'Poppins', sans-serif",
    h1: { fontSize: "2.5rem", fontWeight: 700, color: INK },
    h2: { fontSize: "2rem", fontWeight: 700, color: INK },
    h3: { fontSize: "1.5rem", fontWeight: 600, color: INK },
    h5: { color: INK },
    body1: { fontSize: "1rem", lineHeight: 1.5, color: INK },
    body2: { fontSize: "0.875rem", color: "#5A6072" },
    caption: { color: "#5A6072" },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: { body: { WebkitFontSmoothing: "antialiased" } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.875rem",
          borderRadius: "6px",
          boxShadow: "none",
          minHeight: 40,
          paddingInline: 20,
          transition: "background-color 150ms cubic-bezier(0.23, 1, 0.32, 1)",
          "&:active": { transform: "scale(0.99)" },
          "&:hover": { boxShadow: "none" },
          "&.Mui-focusVisible": { outline: "2px solid #0F9E8E", outlineOffset: 2 },
        },
        sizeSmall: { minHeight: 32, fontSize: "0.8125rem", fontWeight: 600, paddingInline: 14 },
        sizeLarge: { minHeight: 48, fontSize: "0.9375rem", paddingInline: 20 },
        contained: {
          backgroundColor: "#0F9E8E",
          color: "#FFFFFF",
          "&:hover": { backgroundColor: "#0B7F73" },
          "&.Mui-disabled": {
            backgroundColor: "rgba(43, 47, 66, 0.12)",
            color: "#FFFFFF",
          },
        },
        outlined: {
          borderColor: OUTLINE,
          color: INK,
          "&:hover": { borderColor: OUTLINE, backgroundColor: "#EFF1F5" },
          "&.Mui-disabled": { borderColor: "rgba(154, 160, 176, 0.5)", color: OUTLINE },
        },
        text: { "&:hover": { backgroundColor: "#D6F0EC" } },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { "&:hover": { backgroundColor: "#D6F0EC" } },
      },
    },
    MuiPaper: {
      styleOverrides: {
        rounded: { borderRadius: 16 },
        outlined: { border: 0, boxShadow: CARD_SHADOW },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: { borderRadius: 16, boxShadow: CARD_SHADOW, border: 0 },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 16,
          boxShadow: "0 -12px 40px rgba(43, 47, 66, 0.22)",
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paperAnchorRight: { borderRadius: "16px 0 0 16px" },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "#FFFFFF",
          color: INK,
          boxShadow: CARD_SHADOW,
          borderBottom: 0,
        },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          // 48px field height (the 56px MUI default is too tall for dense forms).
          "&:not(.MuiInputBase-sizeSmall) .MuiOutlinedInput-input:not(.MuiInputBase-inputMultiline)": {
            paddingTop: 12.5,
            paddingBottom: 12.5,
          },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: OUTLINE, borderWidth: 1 },
          "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: OUTLINE },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#0F9E8E",
            borderWidth: 2,
          },
          "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#E8382C" },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: "rgba(154, 160, 176, 0.38)",
          },
          "& input": { caretColor: "#0F9E8E" },
          "&.Mui-error input": { caretColor: "#E8382C" },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "#5A6072",
          "&.MuiInputLabel-outlined:not(.MuiInputLabel-shrink):not(.MuiInputLabel-sizeSmall)": {
            transform: "translate(14px, 12px) scale(1)",
          },
          "&.Mui-focused": { color: "#0F9E8E" },
          "&.Mui-error": { color: "#E8382C" },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: { root: { fontSize: "0.75rem", marginInline: 16 } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: "none", fontWeight: 600 } },
    },
  },
});

export default theme;
